import {
  WorkflowRoutineConfig,
  WorkflowRoutineState,
  isNonTerminalIdpStatus,
  useAssetRoutineIdpPoll,
} from '@gripp/shared-logic';
import { useEffect, useState } from 'react';
import { MessageComponent, MessageComponentProps } from '../../../issue/types';
import { CommonRoutineMessageHeader } from '../../components/commonRoutineMessageHeader';
import { IdpTakingLongerHint } from './idpMessageStates';
import { WorkflowMessagePage } from './workflowMessagePage';

/**
 * How long an extraction may run before the reviewer gets told it is slow.
 * Measured from the submission's `createdAt`, not from mount, so a reviewer who
 * opens the Activity ten minutes into a stuck job sees the hint immediately
 * instead of waiting out a fresh grace period.
 */
export const IDP_SLOW_HINT_GRACE_MS = 30_000;

export const WorkflowMessage: MessageComponent = (
  props: MessageComponentProps
) => {
  const assetRoutine = props.message.assetRoutine!;
  const config = assetRoutine.routineVersion.config as WorkflowRoutineConfig;
  const submittedState = assetRoutine.data as WorkflowRoutineState;

  // `idpManaged`/`isIdpSource` survive an IDP off-toggle, so the routine-level
  // flag is what decides whether any of this applies (composite gate, same as
  // the execution side).
  const idpEnabled = config.idpEnabled === true;

  // Poll only while the submitted snapshot still says extraction is running.
  // Every workflow message on an Activity mounts one of these components, so an
  // unconditional poll hook would fire an extra getAssetRoutine query per
  // message; a terminal (or absent) status needs no second fetch at all.
  const isSnapshotWaiting =
    idpEnabled && isNonTerminalIdpStatus(submittedState?.idp?.status);
  const { data: polledState } = useAssetRoutineIdpPoll(
    isSnapshotWaiting ? assetRoutine.id : undefined
  );

  // The polled blob supersedes the submitted one the moment it arrives — this
  // is what turns a status transition into revealed values without a reload.
  const state = polledState ?? submittedState;
  const idpStatus = idpEnabled ? state?.idp?.status : undefined;
  const isExtracting = isNonTerminalIdpStatus(idpStatus);
  const showTakingLongerHint = useIdpSlowHint(
    isExtracting,
    assetRoutine.createdAt
  );

  const responses = state?.responses ?? [];
  const auditLog = state?.auditLog?.auditLog;
  // Copied before sorting — config is a shared reference, and this component
  // re-renders on every poll tick while extraction is in flight.
  const pages = [...config.pages].sort((a, b) => a.order - b.order);

  return (
    <CommonRoutineMessageHeader {...props}>
      {pages.map((page) => (
        <WorkflowMessagePage
          key={page.id}
          page={page}
          responses={responses}
          exceptionsOnly={props.exceptionsOnly}
          assetRoutine={assetRoutine}
          message={props.message}
          auditLog={auditLog}
          idpEnabled={idpEnabled}
          idpStatus={idpStatus}
        />
      ))}
      {showTakingLongerHint && (
        <IdpTakingLongerHint testID="idp-taking-longer" />
      )}
    </CommonRoutineMessageHeader>
  );
};

/**
 * True once an in-flight extraction has been running longer than the grace
 * period. Resolves immediately for a submission that was already past it when
 * the Activity opened, and goes back to false as soon as the status turns
 * terminal.
 */
const useIdpSlowHint = (
  isExtracting: boolean,
  createdAt: Date | undefined
): boolean => {
  const [elapsed, setElapsed] = useState(() =>
    hasOutlivedGrace(isExtracting, createdAt)
  );

  useEffect(() => {
    if (!isExtracting) {
      setElapsed(false);
      return;
    }
    if (hasOutlivedGrace(isExtracting, createdAt)) {
      setElapsed(true);
      return;
    }

    const remaining = IDP_SLOW_HINT_GRACE_MS - sinceCreated(createdAt);
    const timer = setTimeout(() => setElapsed(true), remaining);

    return () => clearTimeout(timer);
  }, [isExtracting, createdAt]);

  return isExtracting && elapsed;
};

/** Milliseconds since submission. `0` when `createdAt` is missing or unparseable,
 * which starts the grace period at mount rather than skipping it. */
const sinceCreated = (createdAt: Date | undefined): number => {
  const createdMs = createdAt ? new Date(createdAt).getTime() : NaN;

  return Number.isNaN(createdMs) ? 0 : Date.now() - createdMs;
};

const hasOutlivedGrace = (
  isExtracting: boolean,
  createdAt: Date | undefined
): boolean => isExtracting && sinceCreated(createdAt) >= IDP_SLOW_HINT_GRACE_MS;
